import { Component, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { Report } from '@app/modules/reporting/services/queries/report';
import { ColorService } from '@app/services/color.service';
import { NotifyService } from '@app/services/notify.service';
import { JobFunnelNameOptions } from '@app/types/translatables/applicant-tracker/job-funnel-name.options';
import { TranslateService } from '@ngx-translate/core';
import { ChartData, MapsReportToChartData } from '../interfaces/MapsReportToChartData.interface';

@Component({
    selector: 'app-current-funnel-status-report',
    templateUrl: './current-funnel-status-report.component.html',
    styleUrls: ['./current-funnel-status-report.component.scss'],
})
export class CurrentFunnelStatusReportComponent implements OnInit, MapsReportToChartData {
    @Input() jobPosting: JobPosting;
    chartData: ChartData;

    isLoading = false;
    reportTitle = 'funnelStatuses';
    report: Report;
    reportMetaData: any;

    constructor(
        private readonly notifyService: NotifyService,
        private readonly reportQuery: Report,
        private readonly translateService: TranslateService,
        private readonly colorService: ColorService
    ) {
        this.translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
            this.loadReport();
        });
    }

    async ngOnInit(): Promise<void> {
        await this.loadReport();
    }

    async loadReport(): Promise<void> {
        this.chartData = await this.fetchReport();
        this.reportMetaData = {
            title: 'Current Funnel Status',
            description: '',
            chartType: 'bar',
            showLegend: true,
            isStacked: true,
            chartData: this.chartData,
        };
    }

    async fetchReport(): Promise<any> {
        this.isLoading = true;
        let chartData: any;

        try {
            this.report = await this.reportQuery
                .name(this.reportTitle)
                .filters([{ name: 'jobPostingId', value: this.jobPosting.id }])
                .page(1)
                .get();

            chartData = this.mapToChart(this.report);
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.isLoading = false;
        }

        return chartData;
    }

    onExport(contentType: string): void {
        // The reason why we are doing this because the report service is a singleton which causes the values to be
        // overwritten by the application source report.
        this.report.name(this.reportTitle);

        this.report.export(contentType).catch(() => {
            this.notifyService.error('applicant-tracker.errorExportingReport');
        });
    }

    mapToChart(reportData: Report): ChartData {
        const sheet = reportData.data.sheets[0];
        return {
            labels: sheet.rows.map((r) => {
                const valueFound = r.cells.find((c) => c.column === 'label').value;

                if (JobFunnelNameOptions[valueFound]) {
                    return this.translateService.instant(JobFunnelNameOptions[valueFound]);
                }

                return valueFound;
            }),
            datasets: [
                {
                    label: this.translateService.instant('applicant-tracker.active'),
                    data: sheet.rows.map((r) => r.cells.find((c) => c.column === 'active').value),
                    backgroundColor: this.colorService.getColorValue('success', 300),
                },
                {
                    label: this.translateService.instant('applicant-tracker.archived'),
                    data: sheet.rows.map((r) => r.cells.find((c) => c.column === 'archived').value),
                    backgroundColor: this.colorService.getColorValue('error', 400),
                },
            ],
            missing: 0,
        };
    }
}
